import { AxiosInstance } from '../../../utilities/requestClient';
import type {
  FetchProspectusRestrictionsTimeSeriesParams,
  ProspectusRestrictionsPageModel,
  ProspectusRestrictionsTimeSeriesResponse,
} from './prospectusRestrictions.types';
import type { StatusChangeTable } from './statusChange.types';
import type {
  LawPageModel,
  LawName,
  LawHistoricalDataModel,
} from './law.types';
import type { ValueAtRiskPageModel } from './valueAtRisk.types';
import type { StressScenarioPageModel } from '../../../components/pages/ucits/stressTests/stressScenario.types';
import type { HistoricalPageModel } from './historicalData.types';
import type { SrriPageModel } from './srri.types';
import type { MrmPageModel } from './mrm.types';
import type { ShareClassHedgeSummaryPageModel } from './shareClassHedgeSummary.types';
import type { CollateralStressTestPageModel } from './collateralStressTest.types';
import type { FundPerformanceAnalysisPageModel } from './fundPerformanceAnalysis.types';
import type { RiskComparisonPageModel } from './riskComparison.types';
import type { RelativeRiskPageModel } from './relativeRisk.types';
import type { SrriShareClassPageModel } from './srriShareClassPage.types';
import type {
  CssfRiskSpreadingPageModel,
  CssfInscopePositionsResponse,
} from './cssfRiskSpreading.types';
import type { SupplementaryRulesPageModel } from './supplementaryRules.types';

export interface FetchStatusChangeParams {
  fundTypes?: string;
  daysBack?: number;
}

export const getDeclineAndFallURL = ({
  fundTypes,
  daysBack,
}: FetchStatusChangeParams): string => {
  const queryParams: string[] = [];

  if (fundTypes?.length) {
    queryParams.push(`schemas=${fundTypes}`);
  }
  if (daysBack) {
    queryParams.push(`days_back=${daysBack}`);
  }

  const queryString = queryParams.length ? `?${queryParams.join('&')}` : '';
  return `decline_and_fall${queryString}`;
};

export const fetchStatusChangeData = async (
  params: FetchStatusChangeParams,
): Promise<StatusChangeTable[]> => {
  const { data } = await AxiosInstance.get<StatusChangeTable[]>(
    getDeclineAndFallURL(params),
  );

  return data;
};

export interface ComparisonUcitsPosition {
  instrument_id: number;
  selected_position_date: string;
  client_price: number;
  bond_future_conversion_factor: number;
  used_fx_rate: number;
  used_position_size: number;
  used_price: number;
  instrument_type_id: number;
  country_code: string;
  used_bc_exposure: number;
  used_risk_factor: number;
  gm_id: number;
  risk_factor: number;
  g_key: string;
  strike: number | null;
  mr_position: number;
  gm_name_long: string;
  fx_rate: number;
  used_delta: number;
  expiry_date: string;
  isin: string;
  accrued_interest_local: number;
  exposure: number;
  accrued_interest_base: number;
  maturity_date: string;
  market_price: number;
  g_name: string;
  base_currency: string;
  [key: string]: any;
}

interface GetComparisonUcitsPositionsParams {
  fundId: string;
  issuer: string;
  activePositionDate: string;
}

export const fetchFundRiskComplianceData = async (
  fundId: string,
  positionDate?: string,
): Promise<ProspectusRestrictionsPageModel> => {
  const { data } = await AxiosInstance.get<ProspectusRestrictionsPageModel>(
    `prospectus_restrictions/overview/${fundId}/${positionDate}`,
  );

  return data;
};

/**
 * GET /prospectus_restrictions/timeseries/<fund_name>
 * Query: rule_index, rule_name, start_date, end_date
 */
export const getProspectusRestrictionsTimeSeriesURL = ({
  fundName,
  ruleIndex,
  ruleName,
  startDate,
  endDate,
}: FetchProspectusRestrictionsTimeSeriesParams): string => {
  const params = new URLSearchParams({
    rule_index: String(ruleIndex),
    rule_name: ruleName,
    start_date: startDate,
    end_date: endDate,
  });
  return `prospectus_restrictions/timeseries/${encodeURIComponent(fundName)}?${params.toString()}`;
};

export const fetchProspectusRestrictionsTimeSeries = async (
  params: FetchProspectusRestrictionsTimeSeriesParams,
): Promise<ProspectusRestrictionsTimeSeriesResponse> => {
  const { data } =
    await AxiosInstance.get<ProspectusRestrictionsTimeSeriesResponse>(
      getProspectusRestrictionsTimeSeriesURL(params),
    );
  return data;
};

export const fetchRiskReportDataKeys = async (
  fundId: string,
): Promise<string[]> => {
  const { data } = await AxiosInstance.get<string[]>(
    `get_risk_report_data_keys/${fundId}`,
  );
  return data;
};

export const getComparisonLawPositionsURL = (
  fundType: string,
  fundId: string,
  issuer: string,
  activePositionDate: string,
) =>
  `/get_t_minus_one_${fundType}_filtered_positions/${fundId}/${encodeURIComponent(issuer)}/${activePositionDate}`;

export const getComparisonUcitsPositions = async ({
  fundId,
  issuer,
  activePositionDate,
}: GetComparisonUcitsPositionsParams): Promise<ComparisonUcitsPosition[]> => {
  const { data } = await AxiosInstance.get<ComparisonUcitsPosition[]>(
    getComparisonLawPositionsURL('ucits', fundId, issuer, activePositionDate),
  );
  return data;
};

/**
 * Fetch consolidated Law page data.
 * GET /get_law/:lawName/:fundName
 */
export const fetchLawPage = async (
  lawName: LawName,
  fundName: string,
  positionDate: string,
): Promise<LawPageModel> => {
  const { data } = await AxiosInstance.get<LawPageModel>(
    `get_law/${lawName}/${fundName}/${positionDate}`,
  );

  return data;
};

/**
 * Fetch consolidated Value at Risk page data.
 * GET /value_at_risk_page/:fundName/:positionDate
 */
export const fetchValueAtRiskPage = async (
  fundName: string,
  positionDate: string,
): Promise<ValueAtRiskPageModel> => {
  const { data } = await AxiosInstance.get<ValueAtRiskPageModel>(
    `value_at_risk_page/${fundName}/${positionDate}`,
  );

  return data;
};

export const getStressScenarioTestsURL = (
  fundName: string,
  selectedPositionDate: string,
) => `get-stress-scenario-tests/${fundName}/${selectedPositionDate}`;

export const fetchStressScenarioTests = async (
  fundName: string,
  selectedPositionDate: string,
): Promise<StressScenarioPageModel> => {
  const { data } = await AxiosInstance.get<StressScenarioPageModel>(
    getStressScenarioTestsURL(fundName, selectedPositionDate),
  );
  return data;
};

export interface FetchHistoricalDataParams {
  fundName: string;
  date1: string;
  date2: string;
  key1: string;
  key2: string;
}

export const getHistoricalDataURL = ({
  fundName,
  date1,
  date2,
  key1,
  key2,
}: FetchHistoricalDataParams): string =>
  `get-historical-data/${fundName}/${date1}/${date2}/${key1}/${key2}`;

export const fetchHistoricalData = async (
  params: FetchHistoricalDataParams,
): Promise<HistoricalPageModel> => {
  const { data } = await AxiosInstance.get<HistoricalPageModel>(
    getHistoricalDataURL(params),
  );

  return data;
};

export interface FetchLawHistoricalDataParams {
  fundName: string;
  startDate: string;
  endDate: string;
}

export const getLawHistoricalDataURL = ({
  fundName,
  startDate,
  endDate,
}: FetchLawHistoricalDataParams): string =>
  `aggregate_monthly_review_d/${fundName}/${startDate}/${endDate}`;

export const fetchLawHistoricalData = async (
  params: FetchLawHistoricalDataParams,
): Promise<LawHistoricalDataModel> => {
  const { data } = await AxiosInstance.get<LawHistoricalDataModel>(
    getLawHistoricalDataURL(params),
  );

  return data;
};

export interface FetchSrriPageParams {
  fundName: string;
  gmidShareclass?: string;
}

export const getSrriPageURL = ({
  fundName,
  gmidShareclass,
}: FetchSrriPageParams): string =>
  gmidShareclass
    ? `srri-page/${fundName}/${gmidShareclass}`
    : `srri-page/${fundName}`;

export const fetchSrriPage = async (
  params: FetchSrriPageParams,
): Promise<SrriPageModel> => {
  const { data } = await AxiosInstance.get<SrriPageModel>(
    getSrriPageURL(params),
  );

  return data;
};

export const getMrmPageURL = (fundName: string): string =>
  `mrm-page/${fundName}`;

export const fetchMrmPage = async (fundName: string): Promise<MrmPageModel> => {
  const { data } = await AxiosInstance.get<MrmPageModel>(
    getMrmPageURL(fundName),
  );

  return data;
};

export interface FetchShareClassHedgeSummaryPageParams {
  positionDate: string;
  fundTypes?: string;
}

export const getShareClassHedgeSummaryPageURL = ({
  positionDate,
  fundTypes,
}: FetchShareClassHedgeSummaryPageParams): string => {
  const queryParams: string[] = [];

  if (fundTypes?.length) {
    queryParams.push(`schemas=${fundTypes}`);
  }

  const queryString = queryParams.length ? `?${queryParams.join('&')}` : '';
  return `get_hedge_ratios/${positionDate}${queryString}`;
};

export const fetchShareClassHedgeSummaryPage = async (
  params: FetchShareClassHedgeSummaryPageParams,
): Promise<ShareClassHedgeSummaryPageModel> => {
  const { data } = await AxiosInstance.get<ShareClassHedgeSummaryPageModel>(
    getShareClassHedgeSummaryPageURL(params),
  );

  return data;
};

export const getCollateralStressTestPageURL = (fundName: string): string =>
  `collateral-stress-test-page/${fundName}`;

export const fetchCollateralStressTestPage = async (
  fundName: string,
): Promise<CollateralStressTestPageModel> => {
  const { data } = await AxiosInstance.get<CollateralStressTestPageModel>(
    getCollateralStressTestPageURL(fundName),
  );

  return data;
};

export interface FetchFundPerformanceAnalysisParams {
  date: string;
  aperiod: string;
  fundName: string;
}

export const getFundPerformanceAnalysisPageURL = ({
  date,
  aperiod,
  fundName,
}: FetchFundPerformanceAnalysisParams): string =>
  `fund-performance-analysis/${date}/${aperiod}/${fundName}`;

export const fetchFundPerformanceAnalysisPage = async (
  params: FetchFundPerformanceAnalysisParams,
): Promise<FundPerformanceAnalysisPageModel> => {
  const { data } = await AxiosInstance.get<FundPerformanceAnalysisPageModel>(
    getFundPerformanceAnalysisPageURL(params),
  );

  return data;
};

export const getRiskComparisonPageURL = (
  fundName: string,
  fromDate: string,
  toDate: string,
): string => `risk_comparison_page/${fundName}/${fromDate}/${toDate}`;

export const fetchRiskComparisonPage = async (
  fundName: string,
  fromDate: string,
  toDate: string,
): Promise<RiskComparisonPageModel> => {
  const { data } = await AxiosInstance.get<RiskComparisonPageModel>(
    getRiskComparisonPageURL(fundName, fromDate, toDate),
  );

  return data;
};

export const getRelativeRiskPageURL = (
  fundName: string,
  positionDate: string,
): string => `relative_risk_page/${fundName}/${positionDate}`;

export const fetchRelativeRiskPage = async (
  fundName: string,
  positionDate: string,
): Promise<RelativeRiskPageModel> => {
  const { data } = await AxiosInstance.get<RelativeRiskPageModel>(
    getRelativeRiskPageURL(fundName, positionDate),
  );

  return data;
};

export const fetchSrriShareClassPage = async (
  fundName: string,
): Promise<SrriShareClassPageModel> => {
  const { data } = await AxiosInstance.get<SrriShareClassPageModel>(
    `get-srri-share-class-page/${fundName}`,
  );

  return data;
};

/**
 * Fetch CSSF risk spreading fund page.
 * GET /stored-cssf-risk-spreading-monitor/:fundName/:positionDate
 */
export const getCssfRiskSpreadingURL = (
  fundName: string,
  positionDate: string,
) => `stored-cssf-risk-spreading-monitor/${fundName}/${positionDate}`;

export const fetchCssfRiskSpreadingPage = async (
  fundName: string,
  positionDate: string,
): Promise<CssfRiskSpreadingPageModel> => {
  const { data } = await AxiosInstance.get<CssfRiskSpreadingPageModel>(
    getCssfRiskSpreadingURL(fundName, positionDate),
  );
  return data;
};

/**
 * Fetch CSSF in-scope positions for a rule (detail panel).
 * GET /cssf_risk_spreading_inscope_positions/:fundName/:ruleNumber/:positionDate?
 */
export const getCssfInscopePositionsURL = (
  fundName: string,
  ruleNumber: number,
  positionDate?: string,
) =>
  `cssf_risk_spreading_inscope_positions/${fundName}/${ruleNumber}${
    positionDate ? `/${positionDate}` : '/'
  }`;

export const fetchCssfInscopePositions = async (
  fundName: string,
  ruleNumber: number,
  positionDate?: string,
): Promise<CssfInscopePositionsResponse> => {
  const { data } = await AxiosInstance.get<CssfInscopePositionsResponse>(
    getCssfInscopePositionsURL(fundName, ruleNumber, positionDate),
  );
  return data;
};

/** GET /upplementary-rules/<fund_name>/<position_date> */
export const getSupplementaryRulesPageURL = (
  fundName: string,
  positionDate: string,
): string => `supplementary-rules/${fundName}/${positionDate}`;

export const fetchSupplementaryRulesPage = async (
  fundName: string,
  positionDate: string,
): Promise<SupplementaryRulesPageModel> => {
  const { data } = await AxiosInstance.get<SupplementaryRulesPageModel>(
    getSupplementaryRulesPageURL(fundName, positionDate),
  );
  return data;
};
